/**
 * SourceRail — desktop-only right rail (Task 067). Shows the LATEST assistant
 * answer's citations. The content is derived from the thread during render by
 * the parent (App computes `sources` + `subtitleQuery`) and passed in — there
 * is no useEffect mirroring thread state into rail state (guidelines §4.2).
 *
 * Visibility is CSS-driven: `.source-rail` is `display: none` below the
 * desktop breakpoint, so this component renders unconditionally and the media
 * query decides whether it shows. No JS breakpoint detection / resize Effect.
 *
 * On mobile the per-answer compact <details> in Thread.tsx carries the sources
 * instead; the two never show at once.
 */
import { useEffect, useRef } from "react";
import type { AnswerCitation } from "../api/answer-client";
import type { Strings } from "../strings";
import { SourceCard } from "./SourceCard";

export interface SourceRailProps {
  strings: Strings;
  /** Latest assistant answer's sources, or null when there is no answer yet. */
  sources: AnswerCitation[] | null;
  /** The user query for the rail subtitle (pending query while loading). */
  subtitleQuery: string | null;
  /** True while an answer is pending — shows the skeleton (Task 071). */
  loading: boolean;
  /** 0-based index of a citation-chip-clicked source to border-highlight (Task 144).
   *  Index (not sourceKey) so exactly ONE card highlights even when sibling cards
   *  share the same path+timestamp. */
  highlightedIndex?: number | null;
}

/**
 * Loading skeleton for the rail (Task 071): a restrained quiver motif —
 * stacked, fading source-card silhouettes that shimmer, led by an east-facing
 * light arrow. The shimmer/arrow motion is gated by prefers-reduced-motion in
 * CSS (collapses to static silhouettes), so no JS reduced-motion check needed.
 */
function RailSkeleton(props: { strings: Strings }): JSX.Element {
  return (
    <div className="rail-skel" role="status" aria-live="polite">
      <div className="rail-skel__note">
        <svg className="rail-skel__arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <line
            x1="3"
            y1="12"
            x2="19"
            y2="12"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <path
            d="M14 7 L19 12 L14 17"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="rail-skel__label">{props.strings.sourceRailLoading}</span>
      </div>
      <ul className="rail-skel__cards" aria-hidden="true">
        <li className="rail-skel__card" />
        <li className="rail-skel__card" />
        <li className="rail-skel__card" />
      </ul>
    </div>
  );
}

export function SourceRail(props: SourceRailProps): JSX.Element {
  const { strings, sources, subtitleQuery, loading, highlightedIndex } = props;
  const railRef = useRef<HTMLElement | null>(null);

  // external: scroll container — when a citation chip selects a source (Task 144),
  // scroll the freshly border-highlighted card into view within the rail. Desktop
  // only in practice: the rail is `display: none` below the breakpoint, so
  // scrollIntoView on a hidden card is a harmless no-op on mobile.
  useEffect(() => {
    if (typeof highlightedIndex !== "number") return;
    const card = railRef.current?.querySelector(".source-rail__item--cited");
    card?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [highlightedIndex]);

  const hasAnswer = sources !== null;
  const isEmpty = hasAnswer && sources.length === 0;
  // Show the subtitle while loading (the pending question) or once answered.
  const showSubtitle = loading || hasAnswer;

  return (
    <aside
      ref={railRef}
      className="source-rail"
      aria-label={strings.threadSourcesLabel}
      data-testid="source-rail"
    >
      <div className="source-rail__head">
        <h2 className="source-rail__label">{strings.threadSourcesLabel}</h2>
        {showSubtitle ? (
          <p className="source-rail__subtitle" title={subtitleQuery ?? undefined}>
            {subtitleQuery !== null && subtitleQuery.length > 0
              ? subtitleQuery
              : strings.sourceRailSubtitle}
          </p>
        ) : null}
      </div>

      {loading ? (
        <RailSkeleton strings={strings} />
      ) : !hasAnswer ? null : isEmpty ? (
        <p className="source-rail__empty">{strings.sourceRailEmpty}</p>
      ) : (
        <ul className="source-rail__list">
          {sources.map((citation, idx) => {
            const cited = highlightedIndex === idx;
            return (
              <li
                key={`${citation.path}-${idx}`}
                className={cited ? "source-rail__item source-rail__item--cited" : "source-rail__item"}
              >
                <SourceCard citation={citation} strings={strings} />
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
